import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { EscalationModel, Escalations, EstimateData, EstimateMetaData, EstimateStatus, LaborMetaData, LaborRates, OtherMetaData, RatesRequestModel, RatesResponseModel, SalesOfficeLaborRates, EscalationData, PricePresentationData } from '../../models/dtos';

import { LaborAndEscalationChangeDialogComponent } from '../../Components/dialogs/common/informative-dialog/labor-and-escalation-change-dialog.component';
import { CommonDataService } from '../common-data.service';
import { ApiPollingService } from './api-polling.service';
import { ILaborAndEscalationChangeDialogData } from '../../models/common/interfaces/ILaborAndEscalationChangeDialogData';
import { CommonMetaDataService } from '../common-meta-data.service';
import { DiscountType, WbsIds } from '../../models/enum';
import { CONSTANTS } from '../../Constants/Constants';

type CompareValue = string | number | boolean | null | undefined | Date | CompareValue[] | object;
type EscalationRateMap = Partial<Record<keyof EscalationModel, EscalationData>>;
type EscalationRateSnapshot = {
  secondYearRate: number | null;
  thirdYearRate: number | null;
  fourthYearRate: number | null;
  fifthYearRate: number | null;
};

@Injectable({
  providedIn: 'root'
})
export class CommonSynchronizationDataService implements OnDestroy {
  private syncPollingSubscription: Subscription = Subscription.EMPTY;


  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly apiPollingService: ApiPollingService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly commonMetaDataService: CommonMetaDataService
  ) { }

  createRatesRequest(): RatesRequestModel | null {
    const estimateData: EstimateData = this.commonDataService.getEstimateData();
    const estimateMetaData: EstimateMetaData = this.commonDataService.getEstimateMetaData();
    const laborMetadata: LaborMetaData = this.commonDataService.getLaborMetaData();
    const otherMetaData: OtherMetaData = this.commonDataService.getOtherMetaData();
    if (!estimateData || !estimateMetaData || !laborMetadata || !otherMetaData) {
      return null;
    }
    const ratesRequest = new RatesRequestModel();
    ratesRequest.salesOfficeIds = laborMetadata.salesOfficeLaborRates.map(rate => rate.salesOfficeId);
    ratesRequest.bookingOfficeId = estimateData.salesOfficeId;
    ratesRequest.business = estimateData.business;
    ratesRequest.segmentId = estimateData.profitCenterId;
    ratesRequest.division = estimateMetaData.estimateSettings.businessUnitId;
    ratesRequest.defaultZineOffice = estimateMetaData.estimateSettings.defaultZineOffice;
    ratesRequest.defaultEngOffice = estimateMetaData.estimateSettings.defaultEngOffice;
    ratesRequest.cultureId = 1;
    ratesRequest.subSegmentId = estimateData.subSegmentID;
    ratesRequest.selectedEngOfficeForECS = 0;
    ratesRequest.selectedDepOfficeForECS = 0;
    ratesRequest.isOldSapSubmitted = false;
    ratesRequest.isEcsApplicable = false;
    ratesRequest.pricePresentationData = otherMetaData.pricePresentationData;
    ratesRequest.useProjectLeadRate = estimateMetaData.estimateSettings.usePL;


    return ratesRequest;
  }

  startSyncPolling(): void {
    this.syncPollingSubscription.unsubscribe();
    this.syncPollingSubscription = this.apiPollingService.isSyncNeeded().subscribe({
      next: (isSyncNeeded) => this.commonDataService.setIsSyncNeeded(isSyncNeeded),
      error: () => this.commonDataService.setIsSyncNeeded(true)
    });
  }

  /**
   * Returns whether synchronization completed and the open estimate may be recalculated.
   * Submitted and read-only estimates retain their saved rates, even after a successful sync.
   */
  async compareLaborRatesAndEscalationRates(syncDataRequest: RatesRequestModel, ratesResponse: RatesResponseModel): Promise<boolean> {
    try {
      if (!this.canApplyRateChanges()) {
        this.commonDataService.setIsSyncNeeded(false);
        this.commonDataService.setLastSyncDate(ratesResponse.lastSyncDate);
        return false;
      }

      const laborRatesProcessed = await this.handleLaborRateChanges(
        syncDataRequest.bookingOfficeId,
        ratesResponse.laborRates
      );

      const escalationRatesProcessed = await this.handleEscalationChanges(ratesResponse.escalationModel);

      const allProcessed = laborRatesProcessed && escalationRatesProcessed;

      if (allProcessed) {
        this.commonDataService.setIsSyncNeeded(false);
        this.commonDataService.setLastSyncDate(ratesResponse.lastSyncDate);
      } else {
        this.commonDataService.setIsSyncNeeded(true);
        this.commonDataService.setLastSyncDate(null);
      }

      return allProcessed;
    } catch (error) {
      console.error('Synchronization processing failed', error);
      this.commonDataService.setIsSyncNeeded(true);
      this.commonDataService.setLastSyncDate(null);
      return false;
    }
  }

  private canApplyRateChanges(): boolean {
    const estimateData = this.commonDataService.getEstimateData();
    return !!estimateData
      && estimateData.estimateStatusId !== EstimateStatus.Submitted
      && !this.commonDataService.isReadOnlyMode();
  }

  private handleLaborRateChanges(bookingOfficeId: number, incomingLaborRates: SalesOfficeLaborRates): Promise<boolean> {
    const currentLaborMetaData: LaborMetaData = this.commonDataService.getLaborMetaData();
    const currentLaborRatesList: SalesOfficeLaborRates = currentLaborMetaData?.salesOfficeLaborRates;

    const currentOfficeRates = this.getOfficeLaborRates(currentLaborRatesList, bookingOfficeId);
    const incomingOfficeRates = this.getOfficeLaborRates(incomingLaborRates, bookingOfficeId);

    return this.processSyncChanges(
      currentOfficeRates,
      incomingOfficeRates,
      this.translate.instant('LABOR_RATE_TITLE'),
      this.translate.instant('LABOR_RATE_CHANGES_MESSAGE'),
      false,
      () => this.applyLaborRates(incomingLaborRates),
      true
    );
  }

  private handleEscalationChanges(incomingEscalationModel: EscalationModel): Promise<boolean> {
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const currentEscalation = estimateMetaData?.escalationRates;

    const filteredCurrentEscalation = this.filterEscalationRates(currentEscalation);
    const filteredIncomingEscalation = this.filterEscalationRates(incomingEscalationModel);

    return this.processSyncChanges(
      filteredCurrentEscalation,
      filteredIncomingEscalation,
      this.translate.instant('ESCALATION_RATE_TITLE'),
      this.translate.instant('ESCALATION_RATE_CHANGES_MESSAGE'),
      true,
      () => this.applyEscalationRates(incomingEscalationModel)
    );
  }

  private processSyncChanges(
    currentValue: CompareValue | LaborRates | null | undefined,
    incomingValue: CompareValue | LaborRates | null | undefined,
    header: string,
    message: string,
    isConfirmative: boolean,
    applyChanges: () => void,
    isLaborRateComparison: boolean = false
  ): Promise<boolean> {
    if (!currentValue || !incomingValue) {
      return Promise.resolve(true);
    }

    let hasChanges = false;

    if (isLaborRateComparison) {
      if (!this.isLaborRates(currentValue) || !this.isLaborRates(incomingValue)) {
        return Promise.resolve(true);
      }

      hasChanges = this.checkForLaborRateChanges(currentValue, incomingValue);
    } else {
      hasChanges = this.checkForDifferences(currentValue as CompareValue, incomingValue as CompareValue);
    }

    if (!hasChanges) {
      return Promise.resolve(true);
    }

    return this.openSyncConfirmationDialog(header, message, isConfirmative).then((isConfirmed) => {
      if (!isConfirmed) {
        return false;
      }

      applyChanges();
      return Promise.resolve(true);
    });
  }

  private openSyncConfirmationDialog(header: string, message: string, isConfirmative: boolean): Promise<boolean> {
    const data: ILaborAndEscalationChangeDialogData = {
      header,
      message,
      isConfirmative
    };

    const dialogRef = this.dialog.open(LaborAndEscalationChangeDialogComponent, {
      width: 'fit-content',
      disableClose: true,
      data
    });

    return firstValueFrom(dialogRef.afterClosed()).then((result) => Boolean(result));
  }

  private parseZoneOfficeIds(ids: string | null | undefined): number[] {
    if (!ids) return [];
    return ids.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
  }

  private isLaborRates(value: CompareValue | LaborRates): value is LaborRates {
    if (!value || typeof value !== 'object' || Array.isArray(value) || value instanceof Date) {
      return false;
    }

    const candidate = value as Partial<LaborRates>;
    return typeof candidate.salesOfficeId === 'number';
  }

  private checkForLaborRateChanges(currentRates: LaborRates, incomingRates: LaborRates): boolean {
    if (!currentRates || !incomingRates) {
      return false;
    }

    const salesOfficeId = currentRates.salesOfficeId;

    // 1. Compare standard base labor rates
    const standardRates = ['projectManagementRates', 'electricianRates', 'engineeringRates', 'specialistRates', 'mechanicalRates'];
    for (const rateField of standardRates) {
      if (this.ratesDiffer(
        currentRates[rateField as keyof LaborRates] as number,
        incomingRates[rateField as keyof LaborRates] as number
      )) {
        return true;
      }
    }

    // 2. Compare base PPM rates
    const basePpmRates = [
      { ppm: 'ppmProjectManagementRates', discount: 'pmDiscntType' },
      { ppm: 'ppmElectricianRates', discount: 'elecDiscntType' },
      { ppm: 'ppmEngineeringRates', discount: 'engDiscntType' },
      { ppm: 'ppmSpecialistRates', discount: 'specDiscntType' },
      { ppm: 'ppmMechanicalRates', discount: 'mechDiscntType' }
    ];

    for (const rate of basePpmRates) {
      if (this.comparePpmRate(rate.ppm, rate.discount, currentRates, incomingRates)) {
        return true;
      }
    }

    // 3. Get office type using MetaData service
    const metaData = this.commonMetaDataService.getMetaData()?.allZoneOfficeIds;
    if (!metaData) {
      return false;
    }
    const ocOfficeIds = this.parseZoneOfficeIds(metaData.oc);
    const zineOfficeIds = this.parseZoneOfficeIds(metaData.zine);
    const isZocOffice = ocOfficeIds.includes(salesOfficeId);
    const isZineOffice = zineOfficeIds.includes(salesOfficeId);

    // 4. Compare office-based specialty rates
    if (isZocOffice && this.checkZocRates(currentRates, incomingRates)) {
      return true;
    }

    if (isZineOffice && this.checkZineRates(currentRates, incomingRates)) {
      return true;
    }

    if (!isZocOffice && !isZineOffice && this.checkOtherOfficeRates(currentRates, incomingRates)) {
      return true;
    }

    // 5. Check WBS special offices
    if (this.checkWbsOfficeRates(salesOfficeId, currentRates, incomingRates)) {
      return true;
    }

    return false;
  }

  private ratesDiffer(rate1: number | undefined | null, rate2: number | undefined | null): boolean {
    return (rate1 ?? 0) !== (rate2 ?? 0);
  }

  private comparePpmRate(ppmField: string, discountField: string, currentRates: LaborRates, incomingRates: LaborRates): boolean {
    const pricePresentationData = this.commonDataService.getOtherMetaData()?.pricePresentationData;
    if (!pricePresentationData) {
      // Incase the pricePresentationData is not available, we will return true to show the new labor rates loaded dialog.
      return true;
    }
    const currentDiscountType = (pricePresentationData[discountField as keyof PricePresentationData] as number) ?? CONSTANTS.ZERO;
    const discountType = DiscountType.DISCOUNT_TYPE_2;
    if (currentDiscountType === discountType) {
      return false;
    }
    return this.ratesDiffer(
      currentRates[ppmField as keyof LaborRates] as number,
      incomingRates[ppmField as keyof LaborRates] as number
    );
  }

  private checkZocRates(currentRates: LaborRates, incomingRates: LaborRates): boolean {
    const zocRates = [
      { rate: 'qualificationEngineeringRates', ppm: 'ppmqEngineeringRates', discount: 'qEngDiscntType' },
      { rate: 'cyberSecEngineeringRates', ppm: 'ppmCEngineeringRates', discount: 'cEngDiscntType' },
      { rate: 'programmingRates', ppm: 'ppmProgRates', discount: 'progDiscntType' },
      { rate: 'graphicsRates', ppm: 'ppmGrapRates', discount: 'grapDiscntType' }
    ];

    for (const item of zocRates) {
      const currentRateValue = currentRates[item.rate as keyof LaborRates] as number;
      const incomingRateValue = incomingRates[item.rate as keyof LaborRates] as number;

      if (currentRateValue !== incomingRateValue) {
        return true;
      }

      if (this.comparePpmRate(item.ppm, item.discount, currentRates, incomingRates)) {
        return true;
      }
    }

    return false;
  }

  private checkZineRates(currentRates: LaborRates, incomingRates: LaborRates): boolean {
    const zineRates = [
      { rate: 'networkEngineeringRates', ppm: 'ppmnEngineeringRates', discount: 'nEngDiscntType' },
      { rate: 'ddsRates', ppm: 'ppmDdsRates', discount: 'ddepDiscntType' }
    ];

    for (const item of zineRates) {
      const currentRateValue = currentRates[item.rate as keyof LaborRates] as number;
      const incomingRateValue = incomingRates[item.rate as keyof LaborRates] as number;

      if (currentRateValue !== incomingRateValue) {
        return true;
      }

      if (this.comparePpmRate(item.ppm, item.discount, currentRates, incomingRates)) {
        return true;
      }
    }

    return false;
  }

  private checkOtherOfficeRates(currentRates: LaborRates, incomingRates: LaborRates): boolean {
    const otherRates = [
      { rate: 'energyEngineeringRates', ppm: 'ppmeEngineeringRates', discount: 'eEngDiscntType' },
      { rate: 'lEngineeringRates', ppm: 'ppmlEngineeringRates', discount: 'lEngDiscntType' }
    ];

    for (const item of otherRates) {
      const currentRateValue = currentRates[item.rate as keyof LaborRates] as number;
      const incomingRateValue = incomingRates[item.rate as keyof LaborRates] as number;

      if (currentRateValue !== incomingRateValue) {
        return true;
      }

      if (this.comparePpmRate(item.ppm, item.discount, currentRates, incomingRates)) {
        return true;
      }
    }

    return false;
  }

  private checkWbsOfficeRates(salesOfficeId: number, currentRates: LaborRates, incomingRates: LaborRates): boolean {
    switch (salesOfficeId) {
      case WbsIds.Programmer: {
        const currentProgRate = currentRates['programmingRates' as keyof LaborRates] as number;
        const incomingProgRate = incomingRates['programmingRates' as keyof LaborRates] as number;

        if (currentProgRate !== incomingProgRate) {
          return true;
        }

        return this.comparePpmRate('ppmProgRates', 'progDiscntType', currentRates, incomingRates);
      }
      case WbsIds.Graphic: {
        const currentGraphRate = currentRates['graphicsRates' as keyof LaborRates] as number;
        const incomingGraphRate = incomingRates['graphicsRates' as keyof LaborRates] as number;

        if (currentGraphRate !== incomingGraphRate) {
          return true;
        }

        return this.comparePpmRate('ppmGrapRates', 'grapDiscntType', currentRates, incomingRates);
      }
      case WbsIds.Dds: {
        const currentDdsRate = currentRates['ddsRates' as keyof LaborRates] as number;
        const incomingDdsRate = incomingRates['ddsRates' as keyof LaborRates] as number;

        if (currentDdsRate !== incomingDdsRate) {
          return true;
        }

        return this.comparePpmRate('ppmDdsRates', 'ddepDiscntType', currentRates, incomingRates);
      }
      default:
        return false;
    }
  }

  private getOfficeLaborRates(rates: SalesOfficeLaborRates, bookingOfficeId: number): LaborRates | undefined {
    if (!rates || !rates.length) {
      return undefined;
    }

    return rates.find((item) => item.salesOfficeId === bookingOfficeId) ?? undefined;
  }

  private isEscalationData(value: unknown): value is EscalationData {
    return !!value
      && typeof value === 'object'
      && 'secondYearRate' in value
      && 'thirdYearRate' in value
      && 'fourthYearRate' in value
      && 'fifthYearRate' in value;
  }

  private getEscalationRateEntries(
    escalation: EscalationModel | Escalations
  ): Array<[keyof EscalationModel, EscalationData]> {
    const escalationRateEntries: Array<[keyof EscalationModel, EscalationData]> = [];
    const rawEntries = Object.entries(escalation);

    for (const [key, value] of rawEntries) {
      if (!this.isEscalationData(value)) {
        continue;
      }

      escalationRateEntries.push([key as keyof EscalationModel, value]);
    }

    return escalationRateEntries;
  }

  private filterEscalationRates(
    escalation: EscalationModel | Escalations | undefined
  ): Record<string, EscalationRateSnapshot> | undefined {
    if (!escalation) {
      return undefined;
    }

    const filtered: Record<string, EscalationRateSnapshot> = {};

    this.getEscalationRateEntries(escalation).forEach(([key, value]) => {

      if (!value) {
        return;
      }

      filtered[key] = {
        secondYearRate: value.secondYearRate,
        thirdYearRate: value.thirdYearRate,
        fourthYearRate: value.fourthYearRate,
        fifthYearRate: value.fifthYearRate
      };
    });

    return filtered;
  }

  private checkForDifferences(currentValue: CompareValue, incomingValue: CompareValue): boolean {
    if (currentValue === incomingValue) {
      return false;
    }

    if (currentValue == null || incomingValue == null) {
      return currentValue !== incomingValue;
    }

    if (currentValue instanceof Date || incomingValue instanceof Date) {
      return new Date(currentValue as string | number | Date).getTime() !== new Date(incomingValue as string | number | Date).getTime();
    }

    if (Array.isArray(currentValue) || Array.isArray(incomingValue)) {
      if (!Array.isArray(currentValue) || !Array.isArray(incomingValue)) {
        return true;
      }

      if (this.isOfficeIdComparableArray(currentValue) && this.isOfficeIdComparableArray(incomingValue)) {
        return this.compareArrayByOfficeId(currentValue, incomingValue);
      }

      if (currentValue.length !== incomingValue.length) {
        return true;
      }

      for (let index = 0; index < currentValue.length; index++) {
        if (this.checkForDifferences(currentValue[index], incomingValue[index])) {
          return true;
        }
      }

      return false;
    }

    if (typeof currentValue !== 'object' || typeof incomingValue !== 'object') {
      return currentValue !== incomingValue;
    }

    const currentObject = currentValue as Record<string, CompareValue>;
    const incomingObject = incomingValue as Record<string, CompareValue>;
    const currentKeys = Object.keys(currentObject);
    const incomingKeys = Object.keys(incomingObject);

    if (currentKeys.length !== incomingKeys.length) {
      return true;
    }

    for (const key of currentKeys) {
      if (!(key in incomingObject)) {
        return true;
      }

      if (this.checkForDifferences(currentObject[key], incomingObject[key])) {
        return true;
      }
    }

    return false;
  }

  private compareArrayByOfficeId(currentValues: CompareValue[], incomingValues: CompareValue[]): boolean {
    if (currentValues.length !== incomingValues.length) {
      return true;
    }

    const incomingByOfficeId = new Map<string | number, CompareValue>();

    for (const incomingItem of incomingValues) {
      const officeId = this.getOfficeIdentifier(incomingItem);
      if (officeId == null || incomingByOfficeId.has(officeId)) {
        return true;
      }

      incomingByOfficeId.set(officeId, incomingItem);
    }

    for (const currentItem of currentValues) {
      const officeId = this.getOfficeIdentifier(currentItem);
      if (officeId == null || !incomingByOfficeId.has(officeId)) {
        return true;
      }

      if (this.checkForDifferences(currentItem, incomingByOfficeId.get(officeId) as CompareValue)) {
        return true;
      }
    }

    return false;
  }

  private isOfficeIdComparableArray(values: CompareValue[]): boolean {
    return values.every((item) => this.getOfficeIdentifier(item) != null);
  }

  private getOfficeIdentifier(value: CompareValue): string | number | null {
    if (!value || typeof value !== 'object' || Array.isArray(value) || value instanceof Date) {
      return null;
    }

    const objectValue = value as Record<string, unknown>;
    const officeId = objectValue['officeId'] ?? objectValue['salesOfficeId'];

    if (typeof officeId === 'number') {
      return officeId;
    }

    return null;
  }

  private applyLaborRates(incomingLaborRates: SalesOfficeLaborRates): void {
    const laborMetaData = this.commonDataService.getLaborMetaData();
    if (!laborMetaData) {
      return;
    }

    const incomingRatesByOfficeId = new Map(
      incomingLaborRates.map((rates) => [rates.salesOfficeId, rates])
    );

    // When the difference is found in the labor rates of the estimate and the labor rates fetched from server db, then instead of
    // blindly replacing the estimate labor rates collection with the incoming rates from the server, we selectively update only 
    // the base labor rates, because the labor rates collection also contains OT rates which should not be overwritten.
    const updatedLaborMetaData: LaborMetaData = {
      ...laborMetaData,
      salesOfficeLaborRates: laborMetaData.salesOfficeLaborRates.map((currentRates) => {
        const incomingRates = incomingRatesByOfficeId.get(currentRates.salesOfficeId);

        if (!incomingRates) {
          return currentRates;
        }

        return {
          ...currentRates,
          projectManagementRates: incomingRates.projectManagementRates,
          engineeringRates: incomingRates.engineeringRates,
          specialistRates: incomingRates.specialistRates,
          mechanicalRates: incomingRates.mechanicalRates,
          electricianRates: incomingRates.electricianRates,
          elecPWRRate: incomingRates.elecPWRRate,
          mechPWRRate: incomingRates.mechPWRRate,
          specPWRRate: incomingRates.specPWRRate,
          ppmProjectManagementRates: incomingRates.ppmProjectManagementRates,
          ppmEngineeringRates: incomingRates.ppmEngineeringRates,
          ppmSpecialistRates: incomingRates.ppmSpecialistRates,
          ppmMechanicalRates: incomingRates.ppmMechanicalRates,
          ppmElectricianRates: incomingRates.ppmElectricianRates,
          ppmProjectLeadDbRate: incomingRates.ppmProjectLeadDbRate,
          ppmProjectManagementDbRate: incomingRates.ppmProjectManagementDbRate,
          networkEngineeringRates: incomingRates.networkEngineeringRates,
          qualificationEngineeringRates: incomingRates.qualificationEngineeringRates,
          cyberSecEngineeringRates: incomingRates.cyberSecEngineeringRates,
          energyEngineeringRates: incomingRates.energyEngineeringRates,
          lEngineeringRates: incomingRates.lEngineeringRates,
          ppmnEngineeringRates: incomingRates.ppmnEngineeringRates,
          ppmqEngineeringRates: incomingRates.ppmqEngineeringRates,
          ppmcEngineeringRates: incomingRates.ppmcEngineeringRates,
          ppmeEngineeringRates: incomingRates.ppmeEngineeringRates,
          ppmlEngineeringRates: incomingRates.ppmlEngineeringRates,
          ppmGrapRates: incomingRates.ppmGrapRates,
          ppmProgRates: incomingRates.ppmProgRates,
          ppmDdsRates: incomingRates.ppmDdsRates,
          projectLeadDbRate: incomingRates.projectLeadDbRate,
          projectManagementDbRate: incomingRates.projectManagementDbRate,
          programmingRates: incomingRates.programmingRates,
          graphicsRates: incomingRates.graphicsRates,
          ddsRates: incomingRates.ddsRates,
          plBurdenDbRate: incomingRates.plBurdenDbRate,
          pmBurdenDbRate: incomingRates.pmBurdenDbRate,
          adjSpecPWRRate: incomingRates.adjSpecPWRRate,
          adjElecPWRRate: incomingRates.adjElecPWRRate,
          adjMechPWRRate: incomingRates.adjMechPWRRate,
          pmBurdenedRate: incomingRates.pmBurdenedRate,
          engBurdenedRate: incomingRates.engBurdenedRate,
          specBurdenedRate: incomingRates.specBurdenedRate,
          mechBurdenedRate: incomingRates.mechBurdenedRate,
          elecBurdenedRate: incomingRates.elecBurdenedRate,
          nEngBurdenedRate: incomingRates.nEngBurdenedRate,
          qEngBurdenedRate: incomingRates.qEngBurdenedRate,
          cEngBurdenedRate: incomingRates.cEngBurdenedRate,
          eEngBurdenedRate: incomingRates.eEngBurdenedRate,
          lEngBurdenedRate: incomingRates.lEngBurdenedRate,
          grapBurdenedRate: incomingRates.grapBurdenedRate,
          progBurdenedRate: incomingRates.progBurdenedRate,
          ddsBurdenedRate: incomingRates.ddsBurdenedRate
        };
      })
    };

    this.commonDataService.patchLaborMetaData(updatedLaborMetaData);
    this.commonDataService.isSaveEnabled = true;
  }

  private applyEscalationRates(incomingEscalation: EscalationModel): void {
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    if (!estimateMetaData) {
      return;
    }

    const currentEscalations = estimateMetaData.escalationRates ?? new Escalations();
    const updatedEscalationRates = this.getEscalationRateEntries(incomingEscalation).reduce<EscalationRateMap>((updatedRates, [key, value]) => {
      const currentValue = currentEscalations[key];

      updatedRates[key] = {
        ...currentValue,
        secondYearRate: value.secondYearRate,
        thirdYearRate: value.thirdYearRate,
        fourthYearRate: value.fourthYearRate,
        fifthYearRate: value.fifthYearRate
      };
      this.commonDataService.isSaveEnabled = true;
      return updatedRates;

    }, {});

    const updatedEscalations: Escalations = {
      ...currentEscalations,
      ...updatedEscalationRates
    };

    this.commonDataService.patchEstimateMetaData({
      ...estimateMetaData,
      escalationRates: updatedEscalations
    });
  }


  ngOnDestroy(): void {
    this.syncPollingSubscription.unsubscribe();
  }
}
